import { getExtensionValue } from "@medplum/core";
import type { Provenance } from "@medplum/fhirtypes";

import type { LaborAmendStageMembershipChangeRecord } from "@/encounters/types/labor-amend-provenance";
import { Extensions } from "@/fhir/enums/extensions";
import { setExtensionValue } from "@/utils/medplum";

/**
 * Serializes labor-stage membership changes onto amend-completion Provenance.
 */
export function setLaborAmendStageMembershipChangesOnProvenance(
  provenance: Provenance,
  changes: LaborAmendStageMembershipChangeRecord[],
): Provenance {
  if (changes.length === 0) {
    return provenance;
  }

  return {
    ...provenance,
    extension: setExtensionValue(
      provenance.extension,
      Extensions.LaborAmendStageMembershipChanges,
      {
        valueString: JSON.stringify(changes),
      },
    ),
  };
}

/**
 * Reads labor-stage membership changes stored on amend-completion Provenance.
 */
export function getLaborAmendStageMembershipChangesFromProvenance(
  provenance: Provenance | undefined,
): LaborAmendStageMembershipChangeRecord[] {
  if (!provenance) {
    return [];
  }

  const raw = getExtensionValue(provenance, Extensions.LaborAmendStageMembershipChanges);
  if (typeof raw !== "string" || raw.length === 0) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isLaborAmendStageMembershipChangeRecord);
  } catch {
    return [];
  }
}

function isLaborAmendStageMembershipChangeRecord(
  value: unknown,
): value is LaborAmendStageMembershipChangeRecord {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as LaborAmendStageMembershipChangeRecord;
  return (
    typeof record.memberObservationId === "string" &&
    record.memberObservationId.length > 0 &&
    typeof record.toStageLoincCode === "string" &&
    record.toStageLoincCode.length > 0 &&
    (record.memberDisplayTitle === undefined || typeof record.memberDisplayTitle === "string")
  );
}
